/**
 * @namespace home
 * @module Page/home
 * @file home.js
 * @since 3.0.0
 * @memberof Page
 * @author jsaldivar
 */

import {
    i18n,
    loading,
    outputDiv,
    siteInfoLang,
    appDomain,
} from "commonAssets/js/constants";
import { createElement } from "commonAssets/lib/createElement.js";
import { loadingGif } from "commonAssets/lib/loadingGif.js";
import { modal } from "commonAssets/lib/modal";
import { createOfficialBanner } from "commonAssets/lib/officialBanner.js";

/**
 * Function to render the home page.
 *
 * @param {Object} params - The parameters for the home page.
 * @param {string} params.slug - The slug of the page.
 * @param {Array} params.pages - The array of pages.
 * @param {Object} params.dates - The dates object.
 * @param {Function} params.dates.card - The function to render the important dates card.
 * @param {HTMLElement} outputDiv - The output div element.
 * @param {HTMLElement} loading - The loading element.
 */
export const homePage = (params) => {
    console.log("[Home] ...");
    // Get the page slug from the URL and initialize the page object
    const slug = params.slug;
    const page = params.pages.find((page) => page.PageName === slug);
    // HTML elements for the page
    const pageTitle = createElement(
        "h2",
        {
            class: "page-title mt-2 mb-4 d-none",
            "aria-label": i18n.getTranslation("home_page_aria_title"),
        },
        [],
        page.PageTitle
    );
    const pageBody = createElement("div", { class: "page-body" });
    const pageAlert = createElement(
        "div",
        {
            class: "alert border-dark bg-light text-dark mb-3 pt-4 text-center",
            id: "pageAlert",
        },
        [],
        page.PageAlert
    );
    const pageHeader = createElement(
        "p",
        { class: "text-center h2 my-5 pageHeader", id: "pageHeader_" + slug },
        [],
        page.PageHeader
    );
    const pageSubHeader1 = createElement(
        "p",
        {
            class: "text-center fw-bold fst-italic pageSubHeader1",
            id: "pageSubHeader1_" + slug,
        },
        [],
        page.PageSubHeader1
    );
    const pageSubHeader2 = createElement(
        "p",
        {
            class: "text-center fst-italic pageSubHeader2",
            id: "pageSubHeader2_" + slug,
        },
        [],
        page.PageSubHeader2
    );
    const divImptDates = createElement(
        "div",
        {
            class: "my-5 mx-auto",
            id: "ImportantDates",
            style: "max-width: 512px;",
        },
        [],
        params.dates.card()
    );
    const pageContent = createElement(
        "div",
        { class: "mb-3 pageContent", id: "pageContent_" + slug },
        [],
        page.PageContent
    );
    const pageFooter = createElement(
        "div",
        { class: "py-3", id: "pageFooter" },
        [],
        page.PageFooter
    );
    // Official Settlement Banner
    const bannerOpts = appDomain?.options?.officialBanner;
    const usePageLead =
        typeof bannerOpts === "object"
            ? bannerOpts.pageLead && bannerOpts.display !== false
            : false;

    const officialBanner = createOfficialBanner({
        i18n,
        siteInfoLang,
        appDomain,
        pageLead: usePageLead ? page.PageLead : null,
    });

    // Legal Rights and Options Table
    const optionsDIV = renderLegalOptionsTable(slug, page);
    const pageElement = createElement("div", { class: "page", id: slug }, [
        officialBanner,
        Boolean(page.PageAlert) ? pageAlert : "",
        pageHeader,
        pageSubHeader1,
        pageSubHeader2,
        divImptDates,
        pageContent,
        optionsDIV,
        pageFooter,
    ]);
    const pageDiv = createElement("div", { class: "home-container" }, [
        pageTitle,
        pageBody,
    ]);
    // Append the page elements to the output div
    outputDiv.appendChild(pageElement);
    outputDiv.appendChild(pageDiv);
    loadingGif(loading, "hide", 100);
};

/**
 * renderLegalOptionsTable()
 *
 * Renders the legal options table.
 *
 * @tags legal options table, table, optionsDIV, optionsTABLE, optionsTbody
 * @param {string} slug - The slug value.
 * @param {object} page - The page object.
 * @returns {HTMLElement} - The rendered table element.
 */
export function renderLegalOptionsTable(slug, page) {
    return createElement(
        "div",
        { class: "table-responsive optionsDIV", id: "optionsDIV_" + slug },
        [
            createElement(
                "table",
                {
                    class: "table table-bordered table-striped table-hover options my-3 optionsTABLE",
                    id: "optionsTABLE_" + slug,
                },
                [
                    createElement("thead", {}, [
                        createElement("tr", {}, [
                            createElement(
                                "th",
                                { colspan: 2, class: "text-center fs-4" },
                                [],
                                page.PageTable.TableTitle
                            ),
                        ]),
                    ]),
                    createElement(
                        "tbody",
                        { class: "optionsTbody" },
                        returnRows(page)
                    ),
                ]
            ),
        ]
    );
}

/**
 * returnRows()
 *
 * Returns an array of table rows with heading and content cells.
 * @returns {HTMLElement[]} - An array of HTML table rows with cells
 */
export function returnRows(page) {
    const rows = [];
    let rowID = 0;
    page.PageTable.TableRows.forEach((row) => {
        const tr = createElement("tr", {}, [
            createElement(
                "th",
                {
                    scope: "row",
                    class: "text-center align-middle p-3",
                    id: "thID_" + rowID,
                },
                [],
                row.Heading
            ),
            createElement(
                "td",
                { class: "p-3", id: "tdID_" + rowID },
                [],
                row.Content
            ),
        ]);
        rows.push(tr);
        rowID++;
    });
    return rows;
}

/**
 * homePageModal()
 *
 * Displays a modal on the home page with the specified page lead.
 * @tags home page, modalShown, notice, page lead
 * @param {Object} params - The parameters object.
 * @param {Array} params.pages - The array of pages.
 * @param {string} params.slug - The slug of the current page.
 */
export function homePageModal(params) {
    // If PageLead is used as the permanent banner (and banner is visible), skip the modal
    const bannerOpts = appDomain?.options?.officialBanner;
    const usePageLead =
        typeof bannerOpts === "object"
            ? bannerOpts.pageLead && bannerOpts.display !== false
            : false;
    if (usePageLead) return;

    const pageLead = params.pages.find(
        (page) => page.PageName === params.slug
    ).PageLead;
    const pageLeadBool = Boolean(pageLead);
    const modalShown = localStorage.getItem("modalShown");
    if (pageLeadBool && !modalShown) {
        modal(i18n.getTranslation("notice"), pageLead);
        localStorage.setItem("modalShown", true);
    }
}
